import * as React from 'react';

/** Material UI Packages. */
import { Box, Typography, ListItem, ListItemText, IconButton, Tooltip } from '@mui/material';

/** Material UI Icons. */
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';

/** Date & Time converting library. */
import dayjs from 'dayjs';

/** Internal Components. */
import { groupContainerStatusMessages, splitHeaderLabel } from 'features/shipments/lib/groupContainerStatusMessages';

/**
 * Size and spacing presets per rendering context.
 *
 * `panel` is the original look of the detail panel. `drawer` renders everything larger for the
 * wide Status drawer: bigger text and icons, tighter action buttons that sit closer to the edge.
 * `actionEdge` is MUI IconButton's `edge`: `end` pulls the button 12px to the right, which the
 * 8px panel padding absorbs; with the drawer's 2px padding it would make the icons overlap.
 */
const RENDER_PRESETS = {
    panel: {
        fontSize: 11,
        actionGap: 1.5,
        actionPadding: '8px',
        actionEdge: 'end',
        actionIconSize: 15,
        actionShiftRight: 0,
    },
    drawer: {
        fontSize: 12,
        actionGap: '1px',
        actionPadding: '2px',
        actionEdge: false,
        actionIconSize: 18,
        actionShiftRight: 20,
    },
};

export const StatusRenderer = ({
    statusMsg,
    cntrList,
    aliasShipmentList,
    statusConfig,
    findWarningStatus,
    onEditStatus,
    onHiddenStatus,
    onDeleteStatus,
    isCaseClosureComplete = false,
    /** 'panel' inside the detail panel, 'drawer' inside the enlarged Status drawer (OS-571). */
    variant = 'panel',
}) => {
    const { fontSize, actionGap, actionPadding, actionEdge, actionIconSize, actionShiftRight } =
        RENDER_PRESETS[variant] ?? RENDER_PRESETS.panel;
    const listItemSx = {
        p: 0,
        ...(actionShiftRight && { '& .MuiListItemSecondaryAction-root': { right: `${16 - actionShiftRight}px` } }),
    };
    /** Function to render the status message text. */
    const renderStatusText = (item, isWarning) => (
        <Typography
            sx={{
                fontSize,
                lineHeight: '1',
                color: isWarning ? 'red' : 'inherit',
                whiteSpace: 'pre-line',
                width: '80%',
            }}
        >
            {isWarning ? item.message : `${dayjs(item.event_date).format('MM/DD')} ${item.message}`}
        </Typography>
    );

    /**
     * Function to render action buttons for status items.
     *
     * A message the manager switched off in Data Entry can no longer be edited, but the
     * entry still has to be clearable, so Hide and Delete stay enabled.
     */
    const renderStatusActions = (item) => {
        const isInactiveMessage = item.active === false;

        return (
            <Box sx={{ display: 'flex', gap: actionGap }}>
                <Tooltip title={isInactiveMessage ? 'This status message has been deactivated' : 'Edit'}>
                    <span>
                        <IconButton
                            edge={actionEdge}
                            sx={{ p: actionPadding }}
                            aria-label="edit"
                            onClick={() => onEditStatus(item.id)}
                            disabled={isCaseClosureComplete || isInactiveMessage}
                        >
                            <EditIcon sx={{ fontSize: actionIconSize }} />
                        </IconButton>
                    </span>
                </Tooltip>
                <Tooltip title="Hide">
                    <span>
                        <IconButton
                            edge={actionEdge}
                            sx={{ p: actionPadding }}
                            aria-label="hide"
                            onClick={() => onHiddenStatus(item.id)}
                            disabled={isCaseClosureComplete}
                        >
                            {item.hidden === true ? (
                                <VisibilityOffIcon sx={{ fontSize: actionIconSize }} />
                            ) : (
                                <VisibilityIcon sx={{ fontSize: actionIconSize }} />
                            )}
                        </IconButton>
                    </span>
                </Tooltip>
                <Tooltip title="Delete">
                    <span>
                        <IconButton
                            edge={actionEdge}
                            sx={{ p: actionPadding }}
                            aria-label="delete"
                            onClick={() => onDeleteStatus(item.id)}
                            disabled={isCaseClosureComplete}
                        >
                            <DeleteIcon sx={{ fontSize: actionIconSize }} />
                        </IconButton>
                    </span>
                </Tooltip>
            </Box>
        );
    };

    /** Function to render the status message items. */
    const renderStatusMessages = (filteredItems) => {
        return filteredItems.map((item, index) => {
            const isWarning = findWarningStatus?.includes(item.status_message_id);
            return (
                <ListItem key={index} secondaryAction={renderStatusActions(item)} sx={listItemSx}>
                    <ListItemText key={index} primary={renderStatusText(item, isWarning)} sx={{ width: '80%' }} />
                </ListItem>
            );
        });
    };

    /** Function to render the status items recorded on each split, under the split's name. */
    const renderAliasMessages = (aliasGroups) => {
        return aliasGroups.map(({ alias, items }, idx) => {
            return (
                <Box key={idx}>
                    {items.length > 0 && (
                        <Typography sx={{ fontSize, fontWeight: 500, color: '#6a994e' }}>
                            {splitHeaderLabel(alias)}
                        </Typography>
                    )}
                    {items.map((val, index) => {
                        const isWarning = findWarningStatus?.includes(val.status_message_id);
                        return (
                            <ListItem key={index} secondaryAction={renderStatusActions(val)} sx={listItemSx}>
                                <ListItemText key={index} primary={renderStatusText(val, isWarning)} />
                            </ListItem>
                        );
                    })}
                </Box>
            );
        });
    };

    return (
        <Box id="status-target-content">
            {cntrList.map((cntr, idx) => {
                const findAliasItems = aliasShipmentList?.filter((item) => item[statusConfig.cntrId] === cntr.id) ?? [];
                const { containerItems, aliasGroups, hasEntries } = groupContainerStatusMessages(
                    statusMsg,
                    cntr.id,
                    findAliasItems,
                );

                if (!hasEntries) return null;

                return (
                    <Box key={idx}>
                        <Typography
                            sx={{
                                fontSize,
                                fontWeight: 600,
                                color: '#003a52',
                                mt: 1,
                            }}
                        >
                            {cntr.name}
                        </Typography>
                        {renderStatusMessages(containerItems)}
                        {renderAliasMessages(aliasGroups)}
                    </Box>
                );
            })}
        </Box>
    );
};
